// The phonepe portal's browser session — every key it stores, in one place, so login, logout and
// an expired-session redirect all clear the same set.
export const PHONEPE_SESSION_KEYS = [
    'phonepeLoginEmail',
    'phonepeCompany',
    'phonepeKycRequired',
    'phonepeBeneficiaries',
    'phonepeEmployeeMail',
    'phonepePhoneNumber',
    'phonepeReimbursementRequired',
    // Patient token from verify-passcode; the Reimbursement API identifies the member by it.
    'phonepeToken',
];

export const clearPhonepeSession = (storage = localStorage) => {
    PHONEPE_SESSION_KEYS.forEach((key) => storage.removeItem(key));
};

export const getPhonepeToken = () => localStorage.getItem('phonepeToken');

// Saves a signed-in member's session from the verify-passcode response. Used by /phonepe/login, and
// by the general /insurance/login for members whose wallet has Reimbursement (isReimbursementRequired):
// whether a member gets Reimbursement comes from their wallet, not from the URL they signed in on.
// The previous member's keys are cleared first, so nothing of theirs (token, beneficiaries) survives.
export const startPhonepeSession = (loginEmail, member = {}, storage = localStorage) => {
    clearPhonepeSession(storage);
    storage.setItem('phonepeLoginEmail', loginEmail);
    if (member.insurance_company) storage.setItem('phonepeCompany', member.insurance_company);
    storage.setItem('phonepeKycRequired', member.kyc_required === false ? 'false' : 'true');
    if (member.beneficiaries) storage.setItem('phonepeBeneficiaries', JSON.stringify(member.beneficiaries));
    if (member.employee_mail) storage.setItem('phonepeEmployeeMail', member.employee_mail);
    if (member.phone_number) storage.setItem('phonepePhoneNumber', member.phone_number);
    storage.setItem('phonepeReimbursementRequired', member.isReimbursementRequired === true ? 'true' : 'false');
    // The Reimbursement API identifies the member by this token alone.
    if (member.token) storage.setItem('phonepeToken', member.token);
};

// The signed-in member's company (their wallet's insurance_company). Sessions saved before the
// company was always stored are phonepe's.
export const getPhonepeCompany = (storage = localStorage) => storage.getItem('phonepeCompany') || 'phonepe';

// Whether an appointment page for `company` may offer Reimbursement: only to a signed-in member
// whose wallet has it, and only on their own company's page.
export const canClaimReimbursement = (company, storage = localStorage) => !!company
    && storage.getItem('phonepeReimbursementRequired') === 'true'
    && String(company).toLowerCase() === getPhonepeCompany(storage).toLowerCase();

// Where the general login sends a member: the dashboard with Reimbursement when their wallet has it.
export const usesReimbursementDashboard = (member) => member?.isReimbursementRequired === true;

// The people on the member's policy, from the wallet's beneficiaries (parallel uids / names /
// relationships arrays): [{ uid, name, relationship }]. A Reimbursement names its patient by one of
// these uids. Some older wallets store uids as a single string.
export const membersFromBeneficiaries = (beneficiaries) => {
    if (!beneficiaries || typeof beneficiaries !== 'object') return [];
    const names = [].concat(beneficiaries.names || []);
    const relationships = [].concat(beneficiaries.relationships || []);
    return [].concat(beneficiaries.uids || [])
        .map((uid, index) => ({ uid, name: names[index] || uid, relationship: relationships[index] || '' }))
        .filter((member) => typeof member.uid === 'string' && member.uid);
};

// The members saved at sign-in (phonepeBeneficiaries); empty when nothing usable was saved.
export const getPhonepeMembers = () => {
    try {
        return membersFromBeneficiaries(JSON.parse(localStorage.getItem('phonepeBeneficiaries') || 'null'));
    } catch (e) {
        return [];
    }
};
